import { useEffect, useRef } from 'react';
import { loadBillingStatus, refreshAccessAfterPayment } from './cloud';

const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 10;

/**
 * Håndterer brugeren, når Stripe sender dem tilbage (`?billing=success|cancel|portal`).
 * Adgangen gives af Stripe-webhooken, ikke af denne side, så vi venter på, at serveren har
 * registreret betalingen, henter nyt token og genindlæser, så det rigtige niveau vises.
 */
export function useBillingReturn(signedIn: boolean, notify: (message: string) => void) {
  const handled = useRef(false);
  // `notify` får ny identitet ved hver render. Via ref afbrydes pollingen ikke af gen-renders.
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  useEffect(() => {
    const notify = (message: string) => notifyRef.current(message);
    if (!signedIn || handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('billing');
    if (!outcome) return;
    handled.current = true;
    params.delete('billing');
    const query = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );

    if (outcome === 'cancel') {
      notify('Betalingen blev afbrudt. Du er ikke blevet trukket.');
      return;
    }
    if (outcome !== 'success') return;

    notify('Tak! Vi aktiverer dit abonnement…');
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < POLL_ATTEMPTS && !cancelled; attempt += 1) {
        try {
          const status = await loadBillingStatus();
          if (status.subscription && status.subscription.tier !== 'free') {
            await refreshAccessAfterPayment();
            if (!cancelled) window.location.reload();
            return;
          }
        } catch {
          // Prøv igen: webhooken kan være få sekunder om at nå frem.
        }
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      if (!cancelled) {
        notify(
          'Betalingen er modtaget. Aktiveringen kan tage et øjeblik. Genindlæs siden om lidt.',
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signedIn]);
}
